import { useState, useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { ArrowRight, Check, Copy, ChevronDown } from 'lucide-react'
import { useScrollReveal, useCountUp } from '@/lib/useScrollReveal'
import {
  modules,
  stats,
  decisionGuide,
  rigorPillars,
  sp80022Validations,
  goldenMasterValidations,
  sp90bEstimators,
  archColumns,
  novelContributions,
} from './homeData'

// ---------------------------------------------------------------------------
// Bitstream hero background — CSS-only animated columns of 0s and 1s
// ---------------------------------------------------------------------------

const STREAM_COLS = 10

function generateBitstreamColumn(len: number, seed: number): string {
  let s = seed
  const chars: string[] = []
  for (let i = 0; i < len; i++) {
    s = ((s * 1103515245 + 12345) & 0x7fffffff) >>> 0
    chars.push(s % 2 === 0 ? '0' : '1')
  }
  return chars.join('\n')
}

const bitstreamColumns = Array.from({ length: STREAM_COLS }, (_, i) => ({
  text: generateBitstreamColumn(60, 7919 + i * 131),
  duration: 18 + (i % 5) * 4,
  delay: -(i * 2.3),
  left: `${6 + i * (88 / STREAM_COLS)}%`,
  opacity: 0.03 + (i % 3) * 0.02,
}))

// ---------------------------------------------------------------------------
// Copy-to-clipboard helper
// ---------------------------------------------------------------------------

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false)
  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [])
  return { copied, copy }
}

// ---------------------------------------------------------------------------
// Section wrapper with scroll reveal
// ---------------------------------------------------------------------------

function RevealSection({
  id,
  children,
  className = '',
  delay = 0,
}: {
  id?: string
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const ref = useScrollReveal<HTMLElement>()
  return (
    <section
      id={id}
      ref={ref}
      className={`reveal-section ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCounter({
  value,
  label,
  suffix,
  delay,
}: {
  value: number
  label: string
  suffix?: string
  delay: number
}) {
  const ref = useCountUp(value, 1500, delay)
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-6">
      <div className="text-3xl sm:text-4xl font-bold font-mono text-primary tracking-tight">
        <span ref={ref}>0</span>
        {suffix && <span>{suffix}</span>}
      </div>
      <div className="text-xs sm:text-sm text-muted-foreground text-center">
        {label}
      </div>
    </div>
  )
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const { copied, copy } = useCopyToClipboard()
  return (
    <button
      onClick={() => copy(text)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-border bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 text-success" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" />
          {label}
        </>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface CitationEntry {
  label: string
  text: string
  bibtex: string
  doi?: string
}

const citations: CitationEntry[] = [
  {
    label: 'Zhang et al., Nature Communications (2025)',
    text: 'R. Zhang, X. Li, C. Wan, R. Hoffmann, M. Hindenberg, Y. Xu, S. Liu, D. Kong, S. Xiong, S. He, A. Vardar, Q. Dai, J. Gong, Y. Sun, Z. Zheng, T. K\u00E4mpfe, G. Yu, X. Han, "Probabilistic greedy algorithm solver using magnetic tunneling junctions for traveling salesman problem," Nature Communications (2025).',
    doi: '10.1038/s41467-025-66864-9',
    bibtex: `@article{zhang_probabilistic_2025,
  title   = {Probabilistic greedy algorithm solver using magnetic tunneling
             junctions for traveling salesman problem},
  journal = {Nature Communications},
  author  = {Zhang, Ran and Li, Xiaohan and Wan, Caihua and Hoffmann, Raik
             and Hindenberg, Meike and Xu, Yingqian and Liu, Shiqiang
             and Kong, Dehao and Xiong, Shilong and He, Shikun
             and Vardar, Alptekin and Dai, Qiang and Gong, Junlu
             and Sun, Yihui and Zheng, Zejie and K\u00E4mpfe, Thomas
             and Yu, Guoqiang and Han, Xiufeng},
  year    = {2025},
  doi     = {10.1038/s41467-025-66864-9}
}`,
  },
  {
    label: 'Zhang et al., Physical Review Applied (2025)',
    text: 'R. Zhang, C. Wan, Y. Xu, X. Li, R. Hoffmann, M. Hindenberg, S. Liu, D. Kong, S. Xiong, S. He, A. Vardar, Q. Dai, J. Gong, Y. Sun, Z. Zheng, T. K\u00E4mpfe, G. Yu, X. Han, "Drift-resilient magnetic-tunnel-junction random-number generator via hybrid control strategies," Physical Review Applied 23, 054073 (2025).',
    doi: '10.1103/PhysRevApplied.23.054073',
    bibtex: `@article{zhang_drift-resilient_2025,
  title   = {Drift-resilient magnetic-tunnel-junction random-number generator
             via hybrid control strategies},
  journal = {Physical Review Applied},
  volume  = {23},
  pages   = {054073},
  author  = {Zhang, Ran and Wan, Caihua and Xu, Yingqian and Li, Xiaohan
             and Hoffmann, Raik and Hindenberg, Meike and Liu, Shiqiang
             and Kong, Dehao and Xiong, Shilong and He, Shikun
             and Vardar, Alptekin and Dai, Qiang and Gong, Junlu
             and Sun, Yihui and Zheng, Zejie and K\u00E4mpfe, Thomas
             and Yu, Guoqiang and Han, Xiufeng},
  year    = {2025},
  doi     = {10.1103/PhysRevApplied.23.054073}
}`,
  },
  {
    label: 'Zhang et al., Advanced Science (2024)',
    text: 'R. Zhang, X. Li, M. Zhao, C. Wan, X. Luo, S. Liu, Y. Zhang, Y. Wang, G. Yu, X. Han, "Probability-Distribution-Configurable True Random Number Generators Based on Spin-Orbit Torque Magnetic Tunnel Junctions," Advanced Science 11, 2402182 (2024).',
    doi: '10.1002/advs.202402182',
    bibtex: `@article{zhang_probabilitydistributionconfigurable_2024,
  title   = {Probability-Distribution-Configurable True Random Number
             Generators Based on Spin-Orbit Torque Magnetic Tunnel Junctions},
  journal = {Advanced Science},
  volume  = {11},
  number  = {23},
  pages   = {2402182},
  author  = {Zhang, Ran and Li, Xiaohan and Zhao, Mingkun and Wan, Caihua
             and Luo, Xuming and Liu, Shiqiang and Zhang, Yu
             and Wang, Yizhan and Yu, Guoqiang and Han, Xiufeng},
  year    = {2024},
  doi     = {10.1002/advs.202402182}
}`,
  },
]

export function HomePage() {
  const [showGoldenMaster, setShowGoldenMaster] = useState(false)

  // Memoize bitstream columns for stable rendering
  const streams = useMemo(() => bitstreamColumns, [])

  return (
    <div className="home-page">
      {/* ================================================================ */}
      {/* Inline styles for animations — keeps everything self-contained   */}
      {/* ================================================================ */}
      <style>{`
        @keyframes bitstream-flow {
          0% { transform: translateY(-50%); }
          100% { transform: translateY(0%); }
        }
        @keyframes hero-glow {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .bitstream-col {
          animation: bitstream-flow linear infinite;
          will-change: transform;
        }
        .hero-title {
          animation: fade-in-up 0.8s ease-out both;
        }
        .hero-subtitle {
          animation: fade-in-up 0.8s ease-out 0.15s both;
        }
        .hero-desc {
          animation: fade-in-up 0.8s ease-out 0.3s both;
        }
        .hero-cta {
          animation: fade-in-up 0.8s ease-out 0.45s both;
        }
        .reveal-section {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .reveal-section.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        .glow-line {
          background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
          animation: hero-glow 3s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .bitstream-col { animation: none !important; }
          .hero-title, .hero-subtitle, .hero-desc, .hero-cta {
            animation: none !important;
            opacity: 1 !important;
          }
          .reveal-section {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
          .glow-line { animation: none !important; opacity: 0.6 !important; }
        }
      `}</style>

      {/* ================================================================ */}
      {/* SECTION 1: Hero                                                  */}
      {/* ================================================================ */}
      <div id="hero" className="relative overflow-hidden border-b border-border">
        {/* Bitstream background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
          {streams.map((col, i) => (
            <div
              key={i}
              className="bitstream-col absolute top-0 font-mono text-[10px] leading-[14px] text-foreground whitespace-pre"
              style={{
                left: col.left,
                opacity: col.opacity,
                animationDuration: `${col.duration}s`,
                animationDelay: `${col.delay}s`,
              }}
            >
              {col.text}
              {'\n'}
              {col.text}
            </div>
          ))}
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>

        {/* Hero content */}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 text-center">
          <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            <span className="font-mono text-primary">BitAssay</span>
          </h1>

          <p className="hero-subtitle text-xl sm:text-2xl text-foreground/90 font-medium mb-4 max-w-3xl mx-auto">
            Browser-Based Statistical Test Suite for Physical Random Number Generators
          </p>

          <p className="hero-desc text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Designed for p-bit devices, hardware entropy sources, and probabilistic computing research.
            Supports both uniform and non-uniform (biased) random number evaluation.
          </p>

          <div className="hero-cta flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#decision-guide"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('decision-guide')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              Start Testing
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#rigor"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-border text-foreground font-medium text-sm hover:bg-accent transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('rigor')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              Explore the Science
            </a>
          </div>
        </div>

        {/* Decorative glow line */}
        <div className="glow-line h-px w-full" />
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Stats Bar                                             */}
      {/* ================================================================ */}
      <RevealSection id="stats" className="border-b border-border bg-card/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-border">
            {stats.map((stat, i) => (
              <StatCounter
                key={stat.label}
                value={stat.value}
                label={stat.label}
                suffix={stat.suffix}
                delay={i * 150}
              />
            ))}
          </div>
        </div>
      </RevealSection>

      {/* ================================================================ */}
      {/* SECTION 3: Decision Guide                                        */}
      {/* ================================================================ */}
      <div id="decision-guide" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <RevealSection>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
            Which Test Suite Do I Need?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
            Choose based on your data type. Each module is self-contained with its own analysis pipeline.
          </p>
        </RevealSection>

        <div className="grid gap-3 sm:gap-4">
          {decisionGuide.map((item, i) => (
            <RevealSection key={item.to} delay={i * 80}>
              <NavLink
                to={item.to}
                className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 p-4 sm:p-5 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
              >
                <div className="flex items-center gap-3 sm:min-w-[220px]">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">{item.dataType}</div>
                    <div className="text-xs text-muted-foreground">{item.dataDetail}</div>
                  </div>
                </div>
                <div className="hidden sm:block text-muted-foreground/40">
                  <ArrowRight className="h-4 w-4" />
                </div>
                <div className="flex-1 text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  {item.suite}
                </div>
                <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
                  Launch &rarr;
                </div>
              </NavLink>
            </RevealSection>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 4: Module Deep Cards                                     */}
      {/* ================================================================ */}
      <div id="modules" className="border-y border-border bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <RevealSection>
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
              Four Specialized Modules
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
              Each module implements a complete, independent test suite with full statistical reporting.
            </p>
          </RevealSection>

          <div className="grid md:grid-cols-2 gap-5">
            {modules.map((mod, i) => (
              <RevealSection key={mod.to} delay={i * 100}>
                <div className="flex flex-col h-full p-6 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                      <mod.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold">{mod.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider ${
                          mod.status === 'Novel'
                            ? 'bg-chart-4/15 text-chart-4'
                            : mod.status === 'Integrated'
                            ? 'bg-success/15 text-success'
                            : 'bg-primary/10 text-primary'
                        }`}>
                          {mod.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">{mod.tests}</p>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {mod.description}
                  </p>

                  <details className="group mb-4">
                    <summary className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer select-none transition-colors">
                      <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
                      View all {mod.testList.length} tests
                    </summary>
                    <ul className="mt-2 ml-5 space-y-1">
                      {mod.testList.map((t) => (
                        <li key={t} className="text-xs text-muted-foreground list-disc">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </details>

                  <div className="mt-auto">
                    <NavLink
                      to={mod.to}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      Launch {mod.title.split(' ')[0]}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </NavLink>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 5: Scientific Rigor                                      */}
      {/* ================================================================ */}
      <div id="rigor" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <RevealSection>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
            Why Trust BitAssay?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
            Every implementation detail is validated against authoritative references.
          </p>
        </RevealSection>

        <div className="grid md:grid-cols-3 gap-6">
          {rigorPillars.map((pillar, i) => (
            <RevealSection key={pillar.title} delay={i * 120}>
              <div className="p-6 rounded-xl border border-border bg-card h-full">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary inline-flex mb-4">
                  <pillar.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold mb-3">{pillar.title}</h3>
                <ul className="space-y-2.5">
                  {pillar.points.map((point, j) => (
                    <li key={j} className="text-xs text-muted-foreground leading-relaxed">
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 6: Cross-Validation Table                                */}
      {/* ================================================================ */}
      <div id="cross-validation" className="border-y border-border bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <RevealSection>
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
              Cross-Validated Against NIST Reference Implementations
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto text-sm">
              Every test produces p-values matching NIST SP 800-22 Rev 1a reference computations.
              Below are exact values from the NIST publication examples.
            </p>
          </RevealSection>

          {/* SP 800-22 Reference Vector Table */}
          <RevealSection delay={100}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              SP 800-22 &mdash; Publication Reference Vectors
            </h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">#</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Test</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground hidden sm:table-cell">Input</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Our p-value</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">NIST Ref</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Digits</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sp80022Validations.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-3 py-2 font-mono text-muted-foreground">{row.id}</td>
                      <td className="px-3 py-2 font-medium">{row.test}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground hidden sm:table-cell text-[11px]">
                        {row.input}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{row.ourPValue}</td>
                      <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                        {row.nistRef}
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{row.digits}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-success/15 text-success">
                          <Check className="h-3 w-3" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealSection>

          {/* Golden Master Validations (expandable) */}
          <RevealSection delay={200} className="mt-8">
            <button
              onClick={() => setShowGoldenMaster(!showGoldenMaster)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showGoldenMaster ? 'rotate-180' : ''}`}
              />
              <span className="font-semibold uppercase tracking-wider text-xs">
                Golden-Master Validation (Tests 4&ndash;15)
              </span>
            </button>

            {showGoldenMaster && (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Test</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Validation Method</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Detail</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goldenMasterValidations.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-3 py-2 font-mono text-muted-foreground">{row.id}</td>
                        <td className="px-3 py-2 font-medium">{row.test}</td>
                        <td className="px-3 py-2 text-muted-foreground">{row.method}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{row.detail}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-success/15 text-success">
                            <Check className="h-3 w-3" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </RevealSection>

          {/* SP 800-90B Estimators */}
          <RevealSection delay={300} className="mt-10">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              SP 800-90B &mdash; 10 Entropy Estimators
            </h3>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Estimator</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Validation</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sp90bEstimators.map((row) => (
                    <tr
                      key={row.name}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-3 py-2 font-medium">{row.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.validation}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-success/15 text-success">
                          <Check className="h-3 w-3" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealSection>

          <RevealSection delay={350} className="mt-6">
            <p className="text-xs text-muted-foreground text-center">
              800+ cross-validation tests across 25 test files run on every commit.
            </p>
          </RevealSection>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 7: Architecture & Privacy                                */}
      {/* ================================================================ */}
      <div id="architecture" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <RevealSection>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
            Privacy by Architecture
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
            BitAssay is designed so that your data physically cannot leave your device.
          </p>
        </RevealSection>

        <div className="grid md:grid-cols-3 gap-6">
          {archColumns.map((col, i) => (
            <RevealSection key={col.title} delay={i * 120}>
              <div className="text-center p-6 rounded-xl border border-border bg-card">
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                    <col.icon className="h-5 w-5" />
                  </div>
                  {col.secondaryIcon && (
                    <>
                      <span className="text-muted-foreground/40 text-lg">/</span>
                      <div className="p-2.5 rounded-lg bg-destructive/10 text-destructive">
                        <col.secondaryIcon className="h-5 w-5" />
                      </div>
                    </>
                  )}
                </div>
                <h3 className="text-base font-semibold mb-2">{col.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {col.description}
                </p>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 8: Novel Contributions                                   */}
      {/* ================================================================ */}
      <div id="novel" className="border-y border-border bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <RevealSection>
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
              What Makes BitAssay Unique
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto text-sm">
              Eight novel contributions to the field of randomness testing &mdash;
              capabilities not available in any other public tool.
            </p>
          </RevealSection>

          <div className="grid sm:grid-cols-2 gap-4">
            {novelContributions.map((item, i) => (
              <RevealSection key={item.id} delay={i * 70}>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors">
                  <span className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg bg-primary/10 text-primary text-xs font-bold font-mono">
                    {item.id}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <h3 className="text-sm font-semibold">{item.title}</h3>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {item.module}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 9: How to Cite                                           */}
      {/* ================================================================ */}
      <div id="cite" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <RevealSection>
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">
            Suggested Citations
          </h2>
          <p className="text-muted-foreground text-center mb-8 max-w-xl mx-auto text-sm">
            If you use BitAssay in your research, we suggest citing the following papers:
          </p>
        </RevealSection>

        <div className="space-y-4">
          {citations.map((cite, i) => (
            <RevealSection key={cite.label} delay={i * 80}>
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="text-sm font-semibold">{cite.label}</h3>
                  {cite.doi && (
                    <a
                      href={`https://doi.org/${cite.doi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-[10px] font-mono text-primary hover:underline"
                    >
                      DOI &rarr;
                    </a>
                  )}
                </div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="rounded-md bg-muted/50 p-3 flex-1 font-mono text-[11px] leading-relaxed select-all text-muted-foreground">
                    {cite.text}
                  </div>
                  <CopyButton text={cite.text} />
                </div>

                <details className="group">
                  <summary className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer select-none transition-colors">
                    <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
                    BibTeX
                  </summary>
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <pre className="rounded-md bg-muted/50 p-3 font-mono text-[10px] leading-relaxed overflow-x-auto select-all flex-1">
                      {cite.bibtex}
                    </pre>
                    <CopyButton text={cite.bibtex} label="Copy" />
                  </div>
                </details>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 10: Footer CTA                                           */}
      {/* ================================================================ */}
      <RevealSection id="get-started" className="border-t border-border">
        <div className="relative overflow-hidden">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.03] to-transparent pointer-events-none" />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to Evaluate Your RNG?
            </h2>
            <p className="text-muted-foreground text-sm mb-10 max-w-md mx-auto">
              All computation runs locally in your browser. No data is uploaded.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {modules.map((mod) => (
                <NavLink
                  key={mod.to}
                  to={mod.to}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all"
                >
                  <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <mod.icon className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    {mod.title}
                  </span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </RevealSection>

    </div>
  )
}
